import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const Landing = () => {
  const navigate = useNavigate();

  return (
    <div className="full-width-container bg-white overflow-y-auto">
      {/* Navigation Bar */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-24 flex items-center justify-between">
          <img
            src="https://cloud.sharemyimage.com/2026/08/16/DayClap-Logo.png"
            alt="DayClap"
            className="h-20 object-contain"
          />
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/login')}
              className="text-sm font-medium text-gray-600 hover:text-gray-900 px-4 py-2 transition-colors"
            >
              Sign In
            </button>
            <button
              onClick={() => navigate('/signup')}
              className="text-sm font-bold text-white bg-gradient-to-r from-pink-500 to-rose-500 px-5 py-2.5 rounded-xl shadow-md shadow-pink-200 hover:shadow-lg transition-all"
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-pink-50/60 via-white to-white -z-0" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-pink-100 rounded-full blur-3xl opacity-40 -z-0" />
        <div className="absolute bottom-10 right-1/4 w-80 h-80 bg-purple-100 rounded-full blur-3xl opacity-30 -z-0" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Text */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-pink-50 border border-pink-100 text-pink-600 text-sm font-semibold px-4 py-2 rounded-full mb-6">
                <SafeIcon name="Heart" className="text-sm" />
                Digital envelopes, not webpages
              </div>

              <h1 className="text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 leading-[1.1] mb-6">
                Send a
                <br />
                <span className="bg-gradient-to-r from-pink-500 via-rose-500 to-red-500 bg-clip-text text-transparent">
                  digital envelope
                </span>
                <br />
                they'll love
              </h1>

              <p className="text-lg text-gray-500 leading-relaxed mb-10 max-w-lg">
                Create → Seal → Send → Receive Envelope → Open → Reply → Receive Reply Envelope.
                Not a webpage. A <em>little digital envelope</em> full of heart.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate('/signup')}
                  className="bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold py-4 px-8 rounded-2xl shadow-lg shadow-pink-200 hover:shadow-xl hover:scale-[1.02] transition-all text-base"
                >
                  Get Started — It's Free
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="bg-white text-gray-700 font-semibold py-4 px-8 rounded-2xl border border-gray-200 hover:border-pink-300 hover:text-pink-600 transition-all text-base"
                >
                  I have an account
                </button>
              </div>

              {/* Social proof */}
              <div className="flex items-center gap-4 mt-10 pt-8 border-t border-gray-100">
                <div className="flex -space-x-3">
                  {['Sarah', 'Mike', 'Emma', 'Alex'].map((name) => (
                    <img
                      key={name}
                      src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${name}`}
                      alt={name}
                      className="w-10 h-10 rounded-full border-2 border-white"
                    />
                  ))}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Loved by thousands</p>
                  <p className="text-xs text-gray-400">Creating special moments every day</p>
                </div>
              </div>
            </motion.div>

            {/* Right: Two Envelope Mockups */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex justify-center lg:justify-end"
            >
              <div className="relative">
                {/* Singles Envelope */}
                <motion.div
                  initial={{ x: 20, rotate: 3 }}
                  animate={{ x: 0, rotate: 3 }}
                  className="w-64 bg-gradient-to-b from-pink-50 to-white rounded-3xl p-5 shadow-xl border border-pink-100 mb-4"
                >
                  <div className="text-center mb-4">
                    <span className="text-3xl">💌</span>
                    <h3 className="text-lg font-bold text-gray-800 mt-2">Singles Invitation</h3>
                    <p className="text-sm text-gray-500 mt-1">Playful & intriguing</p>
                  </div>
                  <div className="bg-white rounded-2xl p-4 shadow-sm border border-pink-100">
                    <p className="text-sm text-gray-600 italic text-center mb-3">
                      "Would you agree to my invitation?"
                    </p>
                    <div className="flex gap-2">
                      <div className="flex-1 bg-green-500 text-white text-center py-2 rounded-lg text-xs font-bold">❤️ Yes</div>
                      <div className="flex-1 bg-amber-500 text-white text-center py-2 rounded-lg text-xs font-bold">🤔 Maybe</div>
                      <div className="flex-1 bg-gray-400 text-white text-center py-2 rounded-lg text-xs font-bold">❌ No</div>
                    </div>
                  </div>
                </motion.div>

                {/* Couples Love Card */}
                <motion.div
                  initial={{ x: -20, rotate: -3 }}
                  animate={{ x: 0, rotate: -3 }}
                  className="w-64 bg-gradient-to-b from-purple-50 to-white rounded-3xl p-5 shadow-xl border border-purple-100 ml-8"
                >
                  <div className="text-center mb-4">
                    <span className="text-3xl">💕</span>
                    <h3 className="text-lg font-bold text-gray-800 mt-2">Couples Love Card</h3>
                    <p className="text-sm text-gray-500 mt-1">Romantic & personal</p>
                  </div>
                  <div className="bg-white rounded-2xl p-4 shadow-sm border border-purple-100">
                    <p className="text-sm text-gray-600 italic text-center mb-3">
                      "Every moment with you is a gift..."
                    </p>
                    <div className="flex items-center gap-2 text-xs text-gray-400 justify-center">
                      <SafeIcon name="Calendar" className="text-purple-400" />
                      <span>Friday, 7:00 PM</span>
                    </div>
                  </div>
                </motion.div>

                {/* Floating elements */}
                <motion.div
                  animate={{ y: [0, -12, 0] }}
                  transition={{ repeat: Infinity, duration: 3 }}
                  className="absolute -top-4 -right-4 bg-white rounded-2xl p-3 shadow-lg"
                >
                  <span className="text-2xl">❤️</span>
                </motion.div>
                <motion.div
                  animate={{ y: [0, 10, 0] }}
                  transition={{ repeat: Infinity, duration: 4 }}
                  className="absolute top-1/2 -left-6 bg-white rounded-2xl p-3 shadow-lg"
                >
                  <span className="text-2xl">✨</span>
                </motion.div>
                <motion.div
                  animate={{ y: [0, -8, 0] }}
                  transition={{ repeat: Infinity, duration: 3.5 }}
                  className="absolute -bottom-2 right-0 bg-white rounded-2xl p-3 shadow-lg"
                >
                  <span className="text-2xl">💌</span>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Two Experiences Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-b from-white to-pink-50/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Two experiences, one envelope system
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              Whether you're making a first move or celebrating your love — DayClap makes it feel like receiving a real envelope.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Singles Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-pink-500 via-rose-500 to-red-500 p-8 rounded-3xl text-white shadow-xl"
            >
              <span className="text-4xl mb-4 block">💌</span>
              <h3 className="text-2xl font-bold mb-3">Singles</h3>
              <p className="text-pink-100 leading-relaxed mb-6">
                A fun invitation game. Create your profile, pick an envelope, write a message, add music,
                and ask the big question. See if they say yes!
              </p>
              <ul className="space-y-2 text-pink-100 text-sm">
                <li>✨ Profile with hobbies & interests</li>
                <li>💌 Envelope templates (free & premium)</li>
                <li>✍️ 140-char message</li>
                <li>🎵 Music selection</li>
                <li>❓ The invitation question</li>
                <li>❤️🤔❌ Yes / Maybe / No responses</li>
              </ul>
            </motion.div>

            {/* Couples Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
              className="bg-gradient-to-br from-purple-500 via-violet-500 to-indigo-500 p-8 rounded-3xl text-white shadow-xl"
            >
              <span className="text-4xl mb-4 block">💕</span>
              <h3 className="text-2xl font-bold mb-3">Couples</h3>
              <p className="text-purple-100 leading-relaxed mb-6">
                A digital love card + date invitation. Write from the heart, add a photo, set the mood with music,
                and optionally plan a date.
              </p>
              <ul className="space-y-2 text-purple-100 text-sm">
                <li>💑 Enter both names</li>
                <li>💌 Romantic envelope templates</li>
                <li>✍️ 300-char heartfelt message</li>
                <li>📸 Photo moment (premium)</li>
                <li>🎵 Music selection</li>
                <li>📅 Optional date plan (date, time, location)</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Envelope Flow */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              The Envelope Experience
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              Don't make it feel like a webpage. Make it feel like someone sent you a little digital envelope.
            </p>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-4 lg:gap-6 max-w-4xl mx-auto">
            {[
              { emoji: '✏️', label: 'Create' },
              { emoji: '📩', label: 'Seal' },
              { emoji: '📤', label: 'Send' },
              { emoji: '💌', label: 'Receive' },
              { emoji: '✨', label: 'Open' },
              { emoji: '💬', label: 'Reply' },
              { emoji: '💌', label: 'Reply Envelope' },
            ].map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center gap-2"
              >
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-pink-100 to-rose-100 rounded-2xl flex items-center justify-center shadow-md mb-2">
                    <span className="text-2xl">{step.emoji}</span>
                  </div>
                  <p className="text-xs font-semibold text-gray-600">{step.label}</p>
                </div>
                {i < 6 && (
                  <SafeIcon name="ArrowRight" className="text-gray-300 text-lg hidden lg:block" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Reply Envelope Highlight */}
      <section className="py-20 lg:py-28 bg-pink-50/50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-white p-10 lg:p-14 rounded-3xl shadow-sm border border-pink-100 text-center"
          >
            <div className="text-5xl mb-6">💌</div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">The Reply Envelope</h3>
            <p className="text-gray-500 text-lg mb-8 max-w-xl mx-auto">
              When they respond, you don't see the whole card again. You receive a <strong>new envelope</strong> containing only their answer.
            </p>
            <div className="bg-pink-50 rounded-2xl p-6 max-w-sm mx-auto border border-pink-100">
              <p className="text-sm text-gray-400 mb-2">Your Question</p>
              <p className="text-lg font-semibold text-gray-800 italic mb-4">"Would you agree to my invitation?"</p>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700rounded-full text-lg font-bold mb-3">
                ❤️ YES
              </div>
              <p className="text-gray-700 italic text-lg mt-3">"I'd love to!"</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Ready to send your first envelope?
          </h2>
          <p className="text-gray-500 text-lg mb-10 max-w-xl mx-auto">
            Join thousands creating memorable moments. It takes less than 2 minutes.
          </p>
          <button
            onClick={() => navigate('/signup')}
            className="bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold py-4 px-10 rounded-2xl shadow-lg shadow-pink-200 hover:shadow-xl hover:scale-[1.02] transition-all text-lg"
          >
            Create Your First Envelope ❤️
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img
                src="https://cloud.sharemyimage.com/2026/08/16/DayClap-Logo.png"
                alt="DayClap"
                className="h-16 opacity-70 object-contain"
              />
            </div>
            <p className="text-sm text-gray-400">
              Create something special for someone special. ❤️
            </p>
            <div className="flex gap-6 text-sm text-gray-400">
              <a href="#" className="hover:text-pink-500 transition-colors">Privacy</a>
              <a href="#" className="hover:text-pink-500 transition-colors">Terms</a>
              <a href="#" className="hover:text-pink-500 transition-colors">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;